import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";

const STORAGE_KEY = "poff.pia.internationalCatalog";

interface PiaCatalogState {
  /**
   * True while the user browses PIA's international product catalogue instead
   * of the catalogue of their profile country.
   */
  international: boolean;
}

function readStoredCatalog(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // storage unavailable (e.g. SSR, private browsing quota exceeded)
    return false;
  }
}

function storeCatalog(international: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(international));
  } catch {
    // storage unavailable
  }
}

/**
 * Which PIA product catalogue is being browsed — the one of the profile
 * country, or the international one.
 *
 * PIA knows a pseudo country `int` that is not a country but a catalogue
 * variant. It must never leak into the profile selection (`userContextStore`):
 * that country goes out as the `country` query parameter to the applications
 * API and as `X-Country-Code` to CoreMedia, where it is authorized against the
 * `country/<X>:read` permissions of the phantom token — `INT` is not among
 * them. The switch therefore lives here, scoped to PIA, and is read by
 * `PiaLocaleService`.
 */
export const piaCatalogStore = signalStore(
  { providedIn: "root" },
  // Factory form: the stored value must be read when the store is created, not
  // when this module is first imported.
  withState<PiaCatalogState>(() => ({ international: readStoredCatalog() })),
  withMethods((store) => ({
    setInternational(international: boolean): void {
      patchState(store, { international });
      storeCatalog(international);
    },
  })),
);
